import React, { FC, useEffect } from "react";
import { Store } from "redux";
import { userRenewed, userSignedOut } from "../actions/authActions";
import { signoutRedirect, userManager } from "./userService";
import { fetchRbacResourceList } from "src/actionCreators/rbacActionCreator";
import { LocalStorageConstants, SessionStorageConstants } from "src/Core/Common/Constants";
import { IsAdminUser } from "src/helper/HelperFunctions";
import { User } from "oidc-client";

type Props = {
    store: Store;
};

const AuthProvider: FC<Props> = ({ store, children }: any) => {
    useEffect(() => {
        const onUserLoaded = (user: User) => {
            if (!sessionStorage.getItem(SessionStorageConstants.IS_SIGN_IN_DIALOG)) {
                store.dispatch(userRenewed(user));
                localStorage.removeItem(LocalStorageConstants.USER_LOGGED_OUT);
                localStorage.setItem(
                    LocalStorageConstants.IS_ADMIN,
                    IsAdminUser(user)
                );
                store.dispatch(fetchRbacResourceList());
                //The local storage is not shared by taskpane and dialog in Outlook online.
                //To workaround the login data sync, pass the user info to taskpane when user is signed in from dialog so can be stored in local storage of taskpane.
                if (!Office.context.ui.displayDialogAsync && Office.context.platform === Office.PlatformType.OfficeOnline) {
                    const regex = /^\/(support|signin-oidc|my-settings\/.*)$/;
                    const isFunctionFileDialog = regex.test(window.location.pathname);
                    isFunctionFileDialog &&
                        Office.context.ui.messageParent(JSON.stringify({ name: `oidc.user:${userManager.settings.authority}:${userManager.settings.client_id}`, value: user.toStorageString() }));
                }
            }
        };

        const onAccessTokenExpiring = () => {
            userManager
                .signinSilent()
                .then(user => {
                    store.dispatch(userRenewed(user));
                })
                .catch(error => {
                    userManager.getUser().then(user => store.dispatch(userRenewed(user)));
                });
        };

        const onAccessTokenExpired = () => {
            signoutRedirect();
        };

        const onUserSignedOut = () => {
            store.dispatch(userSignedOut());
        };

        // events for user
        userManager.events.addUserLoaded(onUserLoaded);
        userManager.events.addAccessTokenExpiring(onAccessTokenExpiring);
        userManager.events.addAccessTokenExpired(onAccessTokenExpired);
        userManager.events.addUserSignedOut(onUserSignedOut);

        // Specify how to clean up after this effect:
        return function cleanup() {
            userManager.events.removeUserLoaded(onUserLoaded);
            userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring);
            userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
            userManager.events.removeUserSignedOut(onUserSignedOut);
        };
    }, [store]);

    return React.Children.only(children);
};

export default AuthProvider;
